import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { Inquiry, ClientFile } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

type File = ClientFile;

const collectionRef = collection(db, "inquiries");

export const InquiryService = {
  async createInquiry(
    inquiryData: Partial<Omit<Inquiry, "id" | "createdAt" | "updatedAt">>,
  ): Promise<string> {
    const inquiryRef = await addDoc(collectionRef, clean({
      ...inquiryData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));
    return inquiryRef.id;
  },

  async getInquiry(inquiryId: string): Promise<Inquiry | null> {
    const inquiryDoc = await getDoc(doc(collectionRef, inquiryId));
    if (inquiryDoc.exists()) {
      return { id: inquiryDoc.id, ...inquiryDoc.data() } as Inquiry;
    }
    return null;
  },

  async getAllInquiries(accountId: string): Promise<Inquiry[]> {
    const inquirySnapshot = await getDocs(
      query(collectionRef, where("accountId", "==", accountId)),
    );
    return inquirySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Inquiry,
    );
  },

  async updateInquiry(
    inquiryId: string,
    updatedData: Partial<Omit<Inquiry, "id" | "createdAt">>,
  ): Promise<void> {
    const inquiryDocRef = doc(collectionRef, inquiryId);
    await updateDoc(inquiryDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteInquiry(inquiryId: string): Promise<void> {
    const inquiryDocRef = doc(collectionRef, inquiryId);
    await deleteDoc(inquiryDocRef);
  },

  async getInquiryFiles(inquiryId: string): Promise<File[]> {
    try {
      const filesSnapshot = await getDocs(
        collection(db, "inquiries", inquiryId, "files"),
      );
      if (filesSnapshot.empty) {
        return [];
      }
      return filesSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as File,
      );
    } catch (error) {
      return [];
    }
  },

  async addFileToInquiry(
    inquiryId: string,
    newFile: Omit<File, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const fileRef = await addDoc(
      collection(db, "inquiries", inquiryId, "files"),
      newFile,
    );
    return fileRef.id;
  },

  async removeParentFromInquiry(
    inquiryId: string,
    parentId: string,
  ): Promise<void> {
    const inquiry = await this.getInquiry(inquiryId);
    if (!inquiry) {
      throw new Error("Inquiry not found");
    }

    const updatedParents = inquiry.parents.filter(parent => parent.id !== parentId);
    await this.updateInquiry(inquiryId, { parents: updatedParents });
  },

  async removeChildFromInquiry(
    inquiryId: string,
    childId: string,
  ): Promise<void> {
    const inquiry = await this.getInquiry(inquiryId);
    if (!inquiry) {
      throw new Error("Inquiry not found");
    }

    const updatedChildren = inquiry.children.filter(child => child.id !== childId);
    await this.updateInquiry(inquiryId, { children: updatedChildren });
  },
};
